import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./Login.css";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { login, isLoggedIn, authLoading, authError, clearError } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [localMessage, setLocalMessage] = useState("");

  const redirectTo = useMemo(() => {
    return searchParams.get("redirect") || location.state?.from?.pathname || "/account";
  }, [searchParams, location.state]);

  useEffect(() => {
    document.title = "Login | MayaChitro Account";

    if (isLoggedIn) {
      navigate("/account", { replace: true });
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    clearError();
    setLocalMessage("");
  }, [clearError]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    if (localMessage) setLocalMessage("");
    if (authError) clearError();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const email = formData.email.trim().toLowerCase();
    const password = formData.password;

    if (!email || !password) {
      setLocalMessage("Please enter your email and password.");
      return;
    }

    const result = await login({ email, password });

    if (result.success) {
      navigate(redirectTo, { replace: true });
    } else {
      setLocalMessage(result.message || "Login failed. Please try again.");
    }
  };

  const messageToShow = authError || localMessage;

  return (
    <main className="login-page">
      <section className="login-shell">
        <div className="login-brand-panel">
          <p className="login-eyebrow">MayaChitro Account</p>

          <h1>Welcome back to your visual marketplace.</h1>

          <p>
            Login to save wishlist items, view orders, access purchased downloads,
            manage your billing email, and control product update preferences.
          </p>

          <div className="login-benefits">
            <div>
              <span>01</span>
              <strong>Access downloads</strong>
              <small>Find purchased photo and wall art files anytime.</small>
            </div>

            <div>
              <span>02</span>
              <strong>Save favorites</strong>
              <small>Build your wishlist before buying or licensing.</small>
            </div>

            <div>
              <span>03</span>
              <strong>Get updates</strong>
              <small>Receive new releases only when you opt in.</small>
            </div>
          </div>
        </div>

        <div className="login-card">
          <div className="login-card-heading">
            <p className="login-eyebrow">Login</p>
            <h2>Sign in to your account</h2>
            <p>Use email login now. Google and Facebook login can be connected later.</p>
          </div>

          <div className="social-login-row">
            <button type="button" disabled>
              <span>G</span>
              Continue with Google
              <small>Coming soon</small>
            </button>

            <button type="button" disabled>
              <span>f</span>
              Continue with Facebook
              <small>Coming soon</small>
            </button>
          </div>

          <div className="login-divider">
            <span>or login with email</span>
          </div>

          {messageToShow && <div className="login-alert">{messageToShow}</div>}

          <form className="login-form" onSubmit={handleSubmit}>
            <label>
              Email address
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </label>

            <label>
              Password
              <div className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </label>

            <div className="login-form-options">
              <label className="remember-row">
                <input type="checkbox" defaultChecked />
                Keep me signed in
              </label>

              <Link to="/forgot-password">Forgot password?</Link>
            </div>

            <button className="login-submit" type="submit" disabled={authLoading}>
              {authLoading ? "Signing in..." : "Login"}
            </button>
          </form>

          <div className="login-footer-note">
            <span>New to MayaChitro?</span>
            <Link to="/register">Create an account</Link>
          </div>

          <div className="login-footer-note admin-login-note">
            <span>Store administrator?</span>
            <Link to="/admin-login">Open secure admin login</Link>
          </div>

          <p className="login-privacy-note">
            Marketing emails are optional. You can choose product updates only after
            clear opt-in.
          </p>
        </div>
      </section>
    </main>
  );
}
